import { useEffect } from "react";

type ClassValue = string | false | null | undefined;

const counts = new WeakMap<Element, Map<string, number>>();

function countsFor(element: Element) {
  let map = counts.get(element);
  if (!map) counts.set(element, (map = new Map<string, number>()));
  return map;
}

function addClass(element: Element, name: string) {
  const map = countsFor(element);
  map.set(name, (map.get(name) ?? 0) + 1);
  element.classList.add(name);
}

function removeClass(element: Element, name: string) {
  const map = countsFor(element);
  const total = map.get(name) ?? 0;
  if (total > 1) {
    map.set(name, total - 1);
  } else {
    map.delete(name);
    element.classList.remove(name);
  }
}

/**
 * Keeps the given classes on `element` while the caller is mounted.
 * Falsy entries are ignored, so `useBodyClass(open && "menu_opened")` works.
 * Classes are reference-counted, so several components may share one class
 * without removing it from under each other.
 */
export function useElementClass(element: Element | null | undefined, ...classNames: ClassValue[]) {
  // The dependency key depends only on the SET of class tokens, so the effect
  // does not re-run when a call site merely re-orders its arguments, repeats a
  // token, or varies the whitespace between them.
  //
  // Note this does not cover a change in membership: in
  // `useElementClass(el, open && "a", "b")`, flipping `open` re-runs the effect
  // and so drops and re-adds "b" as well. Both happen in the same effect flush
  // with no paint in between, so it is not observable; if a call site ever
  // needs to avoid it, diff the token sets in a ref instead of relying on the
  // effect cleanup.
  const key = Array.from(
    new Set(classNames.filter(Boolean).join(" ").split(/\s+/).filter(Boolean))
  )
    .sort()
    .join(" ");

  useEffect(() => {
    if (!element || !key) return;
    const names = key.split(" ");
    names.forEach((name) => addClass(element, name));
    return () => names.forEach((name) => removeClass(element, name));
  }, [element, key]);
}

export function useBodyClass(...classNames: ClassValue[]) {
  useElementClass(document.body, ...classNames);
}
